import { QueryObserverResult } from "@tanstack/react-query"
import { Uuid } from "helpers/types/uuid"
import { useGetQuery } from "hooks/queries"
import { produce } from "immer"
import { createContext, ReactNode, useCallback, useMemo, useReducer } from "react"
import { useAccountAlias } from "resources/account"
import { DeepReadonly } from "ts-essentials"
import { usePreferences } from "hooks/use-preferences"
import DeleteConfirmDialog from "../components/delete-confirm-dialog"
import { useDeleteThread } from "../hooks/delete-thread"
import { useUpdateThread } from "../hooks/update-thread"
import { FetchThreadsResponse, Thread } from "../types/thread"

const PREFERENCE_SUBTYPE = "ai-teammate-thread"
const PREFERENCE_FIELDS = [
  "threadId",
  "threadTitle",
  "isThreadOpen",
]

interface ThreadsState {
  threadId: Uuid | null
  threadTitle: string | null
  isThreadOpen: boolean
  threadIdToDelete: Uuid | null
}

type ThreadsAction = {
  type: "openThread"
  threadId: Uuid
  threadTitle: string
} | {
  type: "newThread"
} | {
  type: "closeThread"
} | {
  type: "setThreadIdToDelete"
  threadIdToDelete: Uuid | null
} | {
  type: "setPreferences"
  preferences: Partial<ThreadsState>
}

const DEFAULT_STATE: ThreadsState = {
  threadId: null,
  threadTitle: null,
  isThreadOpen: false,
  threadIdToDelete: null,
}

function reducer(current: ThreadsState, action: ThreadsAction): ThreadsState {
  return produce(current, draft => {
    switch (action.type) {
      case "openThread":
        draft.threadId = action.threadId
        draft.threadTitle = action.threadTitle
        draft.isThreadOpen = true
        break
      case "newThread":
        draft.threadId = null
        draft.threadTitle = "New Chat"
        draft.isThreadOpen = true
        break
      case "closeThread":
        draft.threadId = null
        draft.threadTitle = null
        draft.isThreadOpen = false
        break
      case "setThreadIdToDelete":
        draft.threadIdToDelete = action.threadIdToDelete
        break
      case "setPreferences":
        Object.assign(draft, action.preferences)
        break
    }
  })
}

function openThread(threadId: Uuid, threadTitle: string) {
  return { type: "openThread" as const, threadId, threadTitle }
}

function newThread() {
  return { type: "newThread" as const }
}

function closeThread() {
  return { type: "closeThread" as const }
}

function setThreadIdToDelete(threadIdToDelete: Uuid | null) {
  return { type: "setThreadIdToDelete" as const, threadIdToDelete }
}

function setPreferences(preferences: Partial<ThreadsState>) {
  return { type: "setPreferences" as const, preferences }
}

export interface ThreadsContextUpdaters {
  openThread: (threadId: Uuid, threadTitle: string) => void
  newThread: () => void
  closeThread: () => void
  setThreadIdToDelete: (threadIdToDelete: Uuid | null) => void
}

export interface ThreadsContextValue extends ThreadsState, ThreadsContextUpdaters {
  threads: Thread[]
  isFetching: boolean
  refetch: () => Promise<QueryObserverResult<FetchThreadsResponse, Error>>
}

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
export const ThreadsContext = createContext<DeepReadonly<ThreadsContextValue>>(null!)

interface ThreadsProviderProps {
  children: ReactNode
}

export function ThreadsProvider({ children }: DeepReadonly<ThreadsProviderProps>) {
  const accountAlias = useAccountAlias()
  const [state, dispatch] = useReducer(reducer, DEFAULT_STATE)

  const isPreferencesLoaded = usePreferences(
    PREFERENCE_SUBTYPE,
    PREFERENCE_FIELDS,
    state,
    dispatch,
    setPreferences,
  )

  const { data, isFetching, refetch } = useGetQuery<FetchThreadsResponse>({
    key: ["ai-teammate-threads", accountAlias],
    url: `/api/${accountAlias}/ai/chat/threads`,
  })

  const { deleteThread: deleteThreadMutation, isDeletingThread } = useDeleteThread()

  const { updateThread, isUpdatingThread } = useUpdateThread()

  const contextUpdaters = useMemo(() => ({
    openThread: (threadId: Uuid, threadTitle: string) => dispatch(openThread(threadId, threadTitle)),
    newThread: () => dispatch(newThread()),
    closeThread: () => dispatch(closeThread()),
    setThreadIdToDelete: (threadIdToDelete: Uuid | null) => dispatch(setThreadIdToDelete(threadIdToDelete)),
  }), [dispatch])

  const handleDeleteThread = useCallback(async () => {
    if (!state.threadIdToDelete) {
      return null
    }
    await deleteThreadMutation({ accountAlias, threadId: state.threadIdToDelete })
    if (state.threadId === state.threadIdToDelete && state.isThreadOpen) {
      contextUpdaters.newThread()
    }
    contextUpdaters.setThreadIdToDelete(null)
  }, [deleteThreadMutation, accountAlias, state.threadId, state.threadIdToDelete, state.isThreadOpen, contextUpdaters])

  const threads = useMemo(() => data?.threads || [], [data])
  const hasThreads = useMemo(() => threads.length > 0, [threads])

  const value = useMemo<ThreadsContextValue>(() => ({
    ...state,
    ...contextUpdaters,
    isThreadOpen: state.isThreadOpen || !hasThreads,
    threads,
    hasThreads,
    isFetching,
    refetch,
    updateThread,
    isUpdatingThread,
    isPreferencesLoaded,
  }), [state, contextUpdaters, threads, hasThreads, isFetching, refetch, updateThread, isUpdatingThread, isPreferencesLoaded])

  return <ThreadsContext.Provider value={value}>
    {children}
    <DeleteConfirmDialog open={!!state.threadIdToDelete} isDeletingThread={isDeletingThread} onClose={() => contextUpdaters.setThreadIdToDelete(null)} onConfirm={handleDeleteThread} />
  </ThreadsContext.Provider>
}
